/**
 * Module responsible to handle Rudderstack SDK load.
 *  - Rudderstack SDK asset is fetched on 'scripts.rudderstack.liquid'.
 *  - OneTrust is integrated with Rudderstack using pre-consent user tracking, this means Ruddertack collect data before the user gave consent
 *     and then send it after the user provides the consent.
 *
 * Documentation:
 *   - OneTrust Integration: https://www.rudderstack.com/docs/data-governance/consent-management/onetrust/javascript/
 *   - Rudderstack Load Options: https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/load-js-sdk/
 *   - Rudderstack Pre-Consent: https://www.rudderstack.com/docs/data-governance/consent-management/onetrust/javascript/#pre-consent-user-tracking
 *
 */

import 'vite/modulepreload-polyfill';

import * as DataDogLogs from '~/lib/datadog/datadog';

// Pull environment types from window object ie: development, production, staging
const ENVIRONMENTS = window.ALLBIRDS.environment.types;

// Map of Rudderstack App keys by environment and shopId, ie: 'production-UK', 'production-US', 'development', 'staging'
const RUDDERSTACK_APP_KEYS = {
  [`${ENVIRONMENTS.PRODUCTION}-UK`]: '1wsEvk2JSySDpu4GkRrODFa3wfu',
  [`${ENVIRONMENTS.PRODUCTION}-US`]: '1wsEr0lioiZzgx5iZd40tVWiUJI',
  [`${ENVIRONMENTS.STAGING}-UK`]: '1rPF2XhPLMKZS39MROI5nrujMIY',
  [`${ENVIRONMENTS.STAGING}-US`]: '1rPF2XhPLMKZS39MROI5nrujMIY',
  [ENVIRONMENTS.DEVELOPMENT]: '1xEmwnbSXytFtBkBAc7P0e79vg1',
};

// Pull resolved environment and shopId and get the App key
const { env, shopId } = window.ALLBIRDS.environment;
const isUsStore = window.ALLBIRDS.environment.isUsStore;
const rudderstackAppKey =
  RUDDERSTACK_APP_KEYS[`${env}-${shopId}`] || RUDDERSTACK_APP_KEYS[ENVIRONMENTS.DEVELOPMENT];

// Constants for Rudderstack
const domain = isUsStore ? 'allbirds.com' : 'allbirds.co.uk';
const RS_CONFIG_URL = `https://apisdk.${domain}`;
const RS_DATA_PLANE_URL = `https://dataplane.${domain}`;
const RS_CONSENT_PROVIDER_ONE_TRUST = 'oneTrust';
const RS_SDK_BASE_URL = `https://jssdk.${domain}/v3`;

let initialized = false;

/**
 * Wrapper function to load the Rudderstack SDK with retry logic
 * Same function signature as the Rudderstack load function
 *
 * @param {string} rudderstackAppKey - Rudderstack App key
 * @param {string} RS_DATA_PLANE_URL - Rudderstack Data Plane URL
 * @param {object} loadOptions - Rudderstack load options
 */
const _loadRudderstackWithRetry = (appKey, dataPlaneUrl, loadOptions) => {
  const SDK_LOADED_ATTEMPTS = 60;
  const SDK_LOADED_ATTEMPTS_INTERVAL_MS = 1000;
  let loadsAttempts = 1;

  const intervalID = setInterval(function () {
    if (window.rudderanalytics && window.rudderanalytics.load) {
      window.rudderanalytics.load(appKey, dataPlaneUrl, loadOptions);

      clearInterval(intervalID);

      DataDogLogs.info(
        `[Allbirds pixel] - Rudderstack SDK - calling sdk.load() function - after ${loadsAttempts} attempts`,
      );
    } else {
      if (loadsAttempts > SDK_LOADED_ATTEMPTS) {
        clearInterval(intervalID);

        DataDogLogs.error(
          `[Allbirds pixel] - Rudderstack SDK - could not be loaded - after ${loadsAttempts} attempts`,
          {},
          new Error('Rudderstack SDK could not be loaded'),
        );
      }

      loadsAttempts++;
    }
  }, SDK_LOADED_ATTEMPTS_INTERVAL_MS);
};

/**
 * Wrapper function to notify consent change to Rudderstack with OneTrust orchestration
 *
 * @private
 */
const _notifyConsentChange = () => {
  // Wrapper function to notify consent change to Rudderstack
  const rudderstackNotifyConsentChange = () => {
    window.ALLBIRDS.Rudderstack.notifyConsentChange(
      window.ALLBIRDS.OneTrust.hasPerformanceConsent(),
    );
  };

  // Check if Rudderstack and OneTrust are loaded otherwise log an error
  if (window.ALLBIRDS.Rudderstack && window.ALLBIRDS.OneTrust) {
    // If OneTrust is not ready, push analytics events into a pool to be triggered once OneTrust is ready
    if (window.ALLBIRDS.OneTrust.isOneTrustActiveGroupsReady()) {
      rudderstackNotifyConsentChange();
    } else {
      window.ALLBIRDS.OneTrust.onConsentChange(() => {
        rudderstackNotifyConsentChange();
      });
    }
  } else {
    DataDogLogs.error(
      '[Allbirds pixel] - Rudderstack SDK - could not notify consent change, OneTrust and Rudderstack SDKs are not loaded',
      {
        windowAllbirdsOneTrust: window.ALLBIRDS.OneTrust,
        windowAllbirdsRudderstack: window.ALLBIRDS.Rudderstack,
      },
      new Error('OneTrust and Rudderstack SDKs are not loaded'),
    );
  }
};

/**
 * Wrapper function for window.rudderanalytics.load with options set to load the Rudderstack SDK
 */
const load = () => {
  if (!initialized) {
    // Load Options: https://www.rudderstack.com/docs/sources/event-streams/sdks/rudderstack-javascript-sdk/load-js-sdk/
    const loadOptions = {
      // The control plane endpoint serving your destination configurations.
      configUrl: RS_CONFIG_URL,

      // Enable consent management with OneTrust
      consentManagement: {
        enabled: true,
        provider: RS_CONSENT_PROVIDER_ONE_TRUST,
      },

      // URL used by the SDK to load its integration SDKs.
      destSDKBaseURL: `${RS_SDK_BASE_URL}/${window.rudderAnalyticsBuildType}/js-integrations`,

      integrations: {
        'Facebook Conversions': {
          // Don't hash Facebook-based values as we already do that for some values.
          // We'll handle this in the transformation when necessary
          hashed: false,
        },
      },

      // Load native integration with Amplitude and Google Analytics
      loadIntegration: false,

      // Internal log level of the SDK
      logLevel: window.ALLBIRDS.settings.rudderstack.logLevel || 'DEBUG',

      // Hook to load event
      onLoaded: () => {
        // Call the consent API to send data to Rudderstack
        _notifyConsentChange();

        DataDogLogs.info('[Allbirds pixel] - Rudderstack SDK - load event');
      },

      // Base URL path used by SDK to load the plugins.
      pluginsSDKBaseURL: `${RS_SDK_BASE_URL}/${window.rudderAnalyticsBuildType}/plugins`,

      // Option to track users as fully anonymous and flush the events after consent is given.
      preConsent: {
        enabled: true,
        events: {
          // Defines SDK's events delivery behavior, other accepted value is "immediate"
          delivery: 'buffer',
        },
        storage: {
          // Defines SDK's cookie storage strategy, other accepted values are "none", "session"
          strategy: 'session',
        },
      },

      // For retrocompatibility use the legacy encryption
      storage: {
        encryption: {
          version: 'legacy',
        },
      },
    };

    // Attempt to load Rudderstack SDK with retry logic
    _loadRudderstackWithRetry(rudderstackAppKey, RS_DATA_PLANE_URL, loadOptions);

    initialized = true;
  }
};

export { load };
